// ============================================================================
// ON-BEHALF SUBMISSION APPROVAL — one vocabulary, every surface
// (operator 2026-08-08)
//
// "all sales are able to submit on behalf of platform handler, though it will
//  require approval from the platform handler."
//
// Five surfaces have to say the same thing about the same order: the create
// form (which flow am I about to start), the orders table (why is this pending
// with nothing happening), the details modal (the handler's decision, the
// requester's wait), the pending-task pool and Task Tracking (the handler's
// inbox card). Each of them derived its own wording once and they drifted —
// which is the failure this codebase's campaign exists to prevent — so the
// wording, and the "may I decide this" rule, live here and nowhere else.
//
// THE AUTHORITY IS ALWAYS THE SERVER. canDecideApproval() decides what to SHOW;
// approve-submission / reject-submission re-derive the same rule fail-closed
// inside their write transaction, against the account's CURRENT handler.
// ============================================================================

import { formatCurrency, formatCryptoAmount } from './helpers';
import { UNATTRIBUTED_HOLDING } from './paymentTask';

export const APPROVAL_TASK_TYPE = 'platform_order_approval';

// "STILL AWAITING THE HANDLER'S DECISION" — the frontend twin of
// backend/lib/platform-approval.js (audit 2026-10 T2). BOTH halves matter:
//   * approval_status 'pending_approval' — nobody has decided yet;
//   * order status 'pending' OR 'paid' — an on-behalf sell may claim a recorded
//     credit before approval and be paid while still undecided; a withdrawn
//     (cancelled / abandoned) submission keeps 'pending_approval' forever but
//     is not waiting on anybody.
// The server's takeover lock, deletion blocker, approve/reject gate, Done Trade,
// Reports and commission sheet ask the SAME question — keep the two identical.
export const AWAITING_DECISION_ORDER_STATUSES = ['pending', 'paid'];
export const isAwaitingApproval = (order) =>
  String(order?.approval_status) === 'pending_approval'
  && AWAITING_DECISION_ORDER_STATUSES.includes(String(order?.status));

export const isRejectedSubmission = (order) =>
  String(order?.approval_status) === 'rejected';

// WHO CAN DECIDE, named from the account's LIVE handler (account_handler_name)
// rather than approval_requested_from_name — the badge must name the person who
// can actually act. The stored "who was asked" is the fallback, and the audit
// trail; the hand-over fail-safe keeps the two identical in practice.
export const approvalHandlerName = (order) =>
  order?.account_handler_name || order?.approval_requested_from_name || 'the account handler';

export const awaitingApprovalBadgeText = (order) =>
  `Awaiting ${approvalHandlerName(order)}'s approval`;

// The approve confirm + its fallback success line, ONE wording for the three
// surfaces that offer Approve (pool card, Task Tracking, order modal). A
// submission that is already PAID (an on-behalf sell that claimed recorded
// credits first) gives finance nothing to do — its finance task is created
// already completed — so the handler's next step is Done Trade.
export const approveSubmissionConfirmText = (alreadyPaid) => (alreadyPaid
  ? 'Approve this submission? Its payment is already in, so finance has nothing to do — you can release the crypto with Done Trade next.'
  : 'Approve this submission? Finance will be asked to handle the money leg.');
export const approveSubmissionDoneText = (resp) =>
  resp?.message || 'Submission approved — finance will handle the money leg.';

// The reject prompt, ONE wording for the three surfaces that offer Reject (pool
// card, Task Tracking, order modal). Returns the reason typed ('' is allowed —
// the reason is optional), or null when the handler cancelled the prompt (send
// nothing).
const REJECT_SUBMISSION_PROMPT = 'Reject this submission? Tell the submitter why (optional):';
export const askRejectReason = () => window.prompt(REJECT_SUBMISSION_PROMPT, '');

// Show the decision affordances only to the current handler. Numeric compare:
// ids arrive as numbers from SQLite but as strings from some form round-trips.
export const canDecideApproval = (order, user) =>
  isAwaitingApproval(order) && Number(order?.account_handled_by) === Number(user?.id);

// The submitter's read-only view of the same state.
export const isMyAwaitingSubmission = (order, user) =>
  isAwaitingApproval(order) && Number(order?.submitted_by) === Number(user?.id);

// ---- WHO COMPLETES THE TRADE (operator 2026-08-12) ------------------------
// "it should be platform handler who ultimately complete the trade (last step)"
//
// The same live fact as the approval gate above — the account's CURRENT
// handler — now also answers "whose button is Done Trade". It lives here beside
// approvalHandlerName for the same reason that one does: one vocabulary, and
// never a second derivation of "who holds this account" on a screen.
//
// MIRRORS THE SERVER EXACTLY, fail-closed: POST /:id/done requires
// platform_accounts.handled_by === you, with no admin override and no submitter
// fallback, so a missing/unknown handler shows NO button rather than one that
// 403s. account_handled_by is projected live by both platform-order GETs.
export const orderHandlerName = (order) =>
  order?.account_handler_name || 'the account handler';

export const isCurrentHandler = (order, user) =>
  order?.account_handled_by != null && Number(order.account_handled_by) === Number(user?.id);

// Not while the submission still awaits the handler's decision (audit 2026-10
// T2): a paid-but-unapproved on-behalf sell must be approved (or rejected)
// first — the server refuses /done with 409 AWAITING_APPROVAL, so the button
// is not offered.
export const canCompleteTrade = (order, user) =>
  String(order?.status) === 'paid' && !isAwaitingApproval(order) && isCurrentHandler(order, user);

// The "Done Trade" confirm — the handler releases the crypto and, on a P2P
// sell, the cash finance booked stops being held as "Unattributed" and becomes
// ours. ONE wording for the order modal button (and anywhere else it is offered).
export const completeTradeConfirmText = (order) => (String(order?.order_type) === 'p2p_sell'
  ? `Release the crypto and complete this trade?\n\n• ${formatCryptoAmount(order?.amount)} ${order?.crypto_type} is deducted from the platform account\n• the ${formatCurrency(order?.fiat_amount)} received becomes ours (it stops being held as "${UNATTRIBUTED_HOLDING}")\n• it is recorded on the checkpoint timeline`
  : 'Mark this trade as completed?');

// What the people who are NOT completing it are told while it waits. Names the
// handler when there is one; when the post is empty it says so — that order
// genuinely cannot move until somebody takes the account over, and the server
// answers the same thing (409 NO_HANDLER). An order still awaiting approval
// says THAT first — it is the step actually in the way.
export const waitingForHandlerText = (order) => {
  if (isAwaitingApproval(order)) {
    return `This submission still needs ${approvalHandlerName(order)}'s approval — approve or reject it first. Done Trade unlocks once it is approved.`;
  }
  return order?.account_handled_by != null
    ? `Waiting for ${orderHandlerName(order)} to release the crypto and complete the trade — they handle this platform account.`
    : 'Nobody is handling this platform account right now, so the crypto cannot be released — it has to be taken over first.';
};

// ---- CREATE FORM: which flow will this submit start? ----------------------
// Accounts come from GET /api/platform-accounts, which already carries
// handler_id + handled_by_name, so the form can tell the operator BEFORE they
// fill anything in. An account with no handler is refused server-side (409
// NO_HANDLER) — say so here rather than let them type a whole order first.
export const accountSubmitMode = (account, user) => {
  if (!account) return 'unknown';
  if (!account.handler_id) return 'no_handler';
  return Number(account.handler_id) === Number(user?.id) ? 'direct' : 'on_behalf';
};

export const accountSubmitNotice = (account, user) => {
  switch (accountSubmitMode(account, user)) {
    case 'direct':
      return { kind: 'direct', text: 'You handle this account — this order goes straight to finance.' };
    case 'on_behalf':
      return {
        kind: 'on_behalf',
        text: `${account.handled_by_name} handles this account. Your order will be sent to them for approval first, ` +
              `and finance is only asked to handle the money once they approve it.`,
      };
    case 'no_handler':
      return {
        kind: 'blocked',
        text: 'Nobody is handling this account — take it over first (or ask whoever is running it to), then submit.',
      };
    default:
      return null;
  }
};
